import React, { useRef, useState, useEffect } from 'react';
import { Play, ChevronLeft, ChevronRight } from 'lucide-react';

const features = [
  { icon: '/spiick_imgs/icons/globe.svg', title: 'Natural acquisition' },
  { icon: '/spiick_imgs/icons/laptop.svg', title: 'Screen-free' },
  { icon: '/spiick_imgs/icons/award.svg', title: 'Active speaking' },
  { icon: '/spiick_imgs/icons/cake-small.svg', title: 'Accessible from preschool' },
  { icon: '/spiick_imgs/icons/folder.svg', title: 'Structured program' },
  { icon: '/spiick_imgs/icons/globe.svg', title: 'Autonomous learning' },
  { icon: '/spiick_imgs/icons/award.svg', title: 'Instant feedback' },
  { icon: '/spiick_imgs/icons/laptop.svg', title: 'AI-powered' }
];

export default function HowItWorks() {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);

  const scrollTo = (index) => {
    const el = trackRef.current;
    if (!el) return;
    const items = el.querySelectorAll('[data-mob-slide]');
    const target = items[Math.max(0, Math.min(features.length - 1, index))];
    if (target) {
      const left = target.offsetLeft - el.offsetLeft - (el.clientWidth - target.clientWidth) / 2;
      el.scrollTo({ left, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const items = el.querySelectorAll('[data-mob-slide]');
        const center = el.scrollLeft + el.clientWidth / 2;
        let bestIdx = 0;
        let bestDist = Infinity;
        items.forEach((node, i) => {
          const c = node.offsetLeft + node.clientWidth / 2 - el.offsetLeft;
          const d = Math.abs(c - center);
          if (d < bestDist) { bestDist = d; bestIdx = i; }
        });
        setActive(bestIdx);
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => { el.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, []);

  return (
    <section id="how" className="relative w-full pt-10 lg:pt-14 pb-10 lg:pb-14 bg-white" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}>
      <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
        <div className="text-center">
          <h2 className="text-[52px] sm:text-[60px] lg:text-[72px] leading-[1.02] text-[#0c0a1a] tracking-tight" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, letterSpacing: '-0.03em' }}>
            How{' '}
            <span className="relative inline-block align-baseline whitespace-nowrap">
              <svg
                aria-hidden="true"
                viewBox="0 0 220 80"
                preserveAspectRatio="none"
                className="absolute -left-[4%] -right-[4%] top-[14%] -bottom-[10%] w-[108%] h-[96%] z-0 pointer-events-none"
              >
                <path
                  d="M9 32 C 30 22, 70 18, 112 16 C 150 14, 190 18, 213 26 C 218 38, 214 54, 208 64 C 175 72, 130 71, 92 70 C 60 70, 24 68, 7 60 C 2 50, 4 38, 9 32 Z"
                  fill="#abf1b2"
                  opacity="0.72"
                />
              </svg>
              <span className="relative z-10" style={{ color: '#004867' }}>it works</span>
            </span>
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-[16px] text-[#555266] leading-relaxed">
            Spiick replaces screens with an interactive pen that listens, talks, and plays — turning every page of our books into a real, joyful conversation.
          </p>
        </div>

        <div className="mt-14 lg:mt-16 grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-3 lg:gap-14 items-center">
          {/* Features — DESKTOP list */}
          <ul className="hidden lg:block lg:order-1 space-y-1.5">
            {features.map((f, i) => (
              <li key={i} className="flex items-center gap-4">
                <span
                  aria-hidden="true"
                  className="shrink-0 w-12 h-12 bg-[#6817FF]"
                  style={{
                    WebkitMaskImage: `url(${f.icon})`,
                    maskImage: `url(${f.icon})`,
                    WebkitMaskRepeat: 'no-repeat',
                    maskRepeat: 'no-repeat',
                    WebkitMaskPosition: 'center',
                    maskPosition: 'center',
                    WebkitMaskSize: 'contain',
                    maskSize: 'contain'
                  }}
                />
                <h3 className="text-[24px] text-[#4221B8] tracking-tight leading-tight">{f.title}</h3>
              </li>
            ))}
          </ul>

          {/* Features — MOBILE carousel */}
          <div className="order-2 lg:hidden -mx-6">
            <div
              ref={trackRef}
              className="flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar px-6 pb-2"
              style={{ scrollbarWidth: 'none' }}
            >
              {features.map((f, i) => (
                <div
                  key={i}
                  data-mob-slide
                  className="snap-center shrink-0 w-[86%] sm:w-[60%] flex flex-col items-center text-center gap-2 py-2"
                >
                  <span
                    aria-hidden="true"
                    className="shrink-0 w-16 h-16 bg-[#6817FF]"
                    style={{
                      WebkitMaskImage: `url(${f.icon})`,
                      maskImage: `url(${f.icon})`,
                      WebkitMaskRepeat: 'no-repeat',
                      maskRepeat: 'no-repeat',
                      WebkitMaskPosition: 'center',
                      maskPosition: 'center',
                      WebkitMaskSize: 'contain',
                      maskSize: 'contain'
                    }}
                  />
                  <h3 className="text-[26px] text-[#4221B8] tracking-tight leading-tight">{f.title}</h3>
                </div>
              ))}
            </div>

            {/* Navigation: arrows + dots */}
            <div className="mt-2 flex flex-col items-center gap-4">
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => scrollTo(active - 1)}
                  aria-label="Previous"
                  disabled={active === 0}
                  className="w-10 h-10 rounded-full border border-[#0c0a1a]/15 grid place-items-center text-[#0c0a1a] hover:bg-[#0c0a1a] hover:text-white hover:border-[#0c0a1a] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => scrollTo(active + 1)}
                  aria-label="Next"
                  disabled={active === features.length - 1}
                  className="w-10 h-10 rounded-full border border-[#0c0a1a]/15 grid place-items-center text-[#0c0a1a] hover:bg-[#0c0a1a] hover:text-white hover:border-[#0c0a1a] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center gap-2">
                {features.map((_, i) => (
                  <button
                    key={i}
                    aria-label={`Go to slide ${i + 1}`}
                    onClick={() => scrollTo(i)}
                    className={`h-1.5 rounded-full transition-all ${i === active ? 'w-5 bg-[#6817FF]' : 'w-1.5 bg-[#0c0a1a]/20'}`}
                  />
                ))}
              </div>
            </div>

            <style>{`.no-scrollbar::-webkit-scrollbar{display:none}`}</style>
          </div>

          {/* Horizontal video block */}
          <div className="order-1 lg:order-2">
            <div className="relative rounded-[24px] lg:rounded-[32px] overflow-hidden aspect-[16/10] w-full">
              <img src="/spiick_imgs/video-cover.png" alt="Spiick demo" className="absolute inset-0 w-full h-full object-cover" />
              <button aria-label="Play" className="absolute inset-0 grid place-items-center group">
                <span className="relative w-20 h-20 rounded-full bg-white grid place-items-center shadow-2xl group-hover:scale-105 transition-transform pulse-ring">
                  <Play className="w-7 h-7 text-[#ffc500] fill-[#ffc500] ml-1" />
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
